import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useDataProvider, useTranslate } from "ra-core";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { EmailDataProvider } from "./api/types";

const BRAND =
  "border-[#F77119]/40 bg-[#F77119]/10 text-foreground [&>svg]:text-[#F77119]";

/** Spec 08 §9.10: warns admins while sends are simulated (fake provider) or SES is still sandboxed. */
export const SesSandboxBanner = () => {
  const translate = useTranslate();
  const dp = useDataProvider<EmailDataProvider>();
  // The Supabase provider gains EmailMethods in a later task; without them, show nothing.
  const available = typeof dp.emailProviderStatus === "function";
  const { data } = useQuery({
    queryKey: ["email", "providerStatus"],
    queryFn: () => dp.emailProviderStatus(),
    staleTime: 60_000,
    enabled: available,
  });
  if (!data) return null;
  // B5-T23: no SES credentials yet. Drafts, templates, segments and scheduling still work.
  if (data.configured === false)
    return (
      <Alert className={BRAND}>
        <TriangleAlert />
        <AlertDescription className="text-foreground">
          {translate("gavion.email.common.sandbox.not_configured")}
        </AlertDescription>
      </Alert>
    );
  if (data.fake)
    return (
      <Alert className={BRAND}>
        <TriangleAlert />
        <AlertDescription className="text-foreground">
          {translate("gavion.email.common.sandbox.fake")}
        </AlertDescription>
      </Alert>
    );
  if (data.productionAccess === false)
    return (
      <Alert className={BRAND}>
        <TriangleAlert />
        <AlertTitle>
          {translate("gavion.email.common.sandbox.title")}
        </AlertTitle>
        <AlertDescription className="text-foreground">
          {translate("gavion.email.common.sandbox.body")}
        </AlertDescription>
      </Alert>
    );
  return null;
};
